import React, { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios"; // Import axios
import { addMatch, updateMatch } from '../../../services/matchService'; // Add this import at the top
import VideosTab from './VideosTab';

const EventsTab = ({
  matches: initialMatches, // Assuming matches is passed as a prop
  sports,
  homeTeams,
  awayTeams,
  handleAdd,
  handleEdit,
  handleDelete,
}) => {
  const [matches, setMatches] = useState([]);
  const [activeTab, setActiveTab] = useState("matches");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [scores, setScores] = useState({}); // Change to object to store scores by match ID
  const [newMatch, setNewMatch] = useState({
    sportId: "",
    date: "",
    location: "",
    homeTeamId: "",
    awayTeamId: "",
    leagueId: "",
  });
  const [showAddScoreForm, setShowAddScoreForm] = useState(null);
  const [fullTimeScoreMatch, setFullTimeScoreMatch] = useState(null);
  const [fullTimeScore, setFullTimeScore] = useState({ homeTeamScore: "", awayTeamScore: "" });
  const [fullTimeScorePending, setFullTimeScorePending] = useState(false);
  const [players, setPlayers] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState(null);

  const getOppositeTeamId = (match, teamId) => {
    if (!match || !teamId) return null;
    return Number(teamId) === Number(match.homeTeamId)
      ? match.awayTeamId
      : match.homeTeamId;
  };

  const fetchPlayersForScorer = async (match, teamId, isOwnGoal) => {
    const sourceTeamId = isOwnGoal ? getOppositeTeamId(match, teamId) : teamId;
    if (sourceTeamId) {
      await fetchPlayers(sourceTeamId);
    } else {
      setPlayers([]);
    }
  };
  const [playerNames, setPlayerNames] = useState({});
  const [leagues, setLeagues] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    addMatch: false,
    editMatch: false,
    deleteMatch: false,
    addScore: false,
    fetchMatches: false
  });

  // New state for match statistics
  const [shotsOnTarget, setShotsOnTarget] = useState({});
  const [saves, setSaves] = useState({});
  const [clearances, setClearances] = useState({});
  const [cards, setCards] = useState({});
  const [penaltyMisses, setPenaltyMisses] = useState({});
  const [showAddStatsForm, setShowAddStatsForm] = useState(null);
  const [statsFormData, setStatsFormData] = useState({
    type: "", // 'shot', 'save', 'clearance', 'card'
    playerId: "",
    minute: "",
    cardType: "", // 'yellow' or 'red' for cards
    reason: "", // for cards
  });

  const [homeStarting11, setHomeStarting11] = useState([]);
  const [homeSubs, setHomeSubs] = useState([]);
  const [awayStarting11, setAwayStarting11] = useState([]);
  const [awaySubs, setAwaySubs] = useState([]);
  const [homePlayers, setHomePlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [substitutes, setSubstitutes] = useState({});
  const [showSubstitutesModal, setShowSubstitutesModal] = useState(null);

  // Local delete handler to sync local state with parent deletion
  const handleDeleteMatch = async (match) => {
    try {
      setLoadingStates((prev) => ({ ...prev, deleteMatch: true }));
      await handleDelete(match);
      setMatches((prev) => prev.filter((m) => m.matchesId !== match.matchesId));
      toast.success('Match deleted');
    } catch (error) {
      console.error('Error deleting match:', error);
      toast.error('Failed to delete match');
    } finally {
      setLoadingStates((prev) => ({ ...prev, deleteMatch: false }));
    }
  };

  useEffect(() => {
    console.log('Initial matches prop:', initialMatches); // Debug log
    
    const initializeData = async () => {
      try {
        if (leagues.length === 0) {
          await fetchLeagues();
        }

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/matches`);
        if (response.data && Array.isArray(response.data)) {
          const enrichedMatches = await fetchMatchDetails(response.data);
          setMatches(enrichedMatches);
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        toast.error('Failed to load data');
      }
    };

    initializeData();
  }, []); // Empty dependency array for initial load only

  useEffect(() => {
    console.log('Matches state updated:', matches); // Debug log
  }, [matches]);

  const fetchScores = async (matchId) => {
    if (scores[matchId]) return; // If scores exist, don't fetch again
  
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/scores/matches/${matchId}/scores`
      );
      setScores((prev) => ({
        ...prev,
        [matchId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching scores:", error);
      toast.error("Failed to fetch scores.");
    }
  };

  // New API functions for match statistics
  const fetchShotsOnTarget = async (matchId) => {
    if (shotsOnTarget[matchId]) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/shots-on-target/match/${matchId}`
      );
      setShotsOnTarget((prev) => ({
        ...prev,
        [matchId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching shots on target:", error);
      // Don't show error toast as this endpoint might not exist yet
    }
  };

  const fetchSaves = async (matchId) => {
    if (saves[matchId]) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/saves/match/${matchId}`
      );
      setSaves((prev) => ({
        ...prev,
        [matchId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching saves:", error);
    }
  };

  const fetchClearances = async (matchId) => {
    if (clearances[matchId]) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/clearances/match/${matchId}`
      );
      setClearances((prev) => ({
        ...prev,
        [matchId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching clearances:", error);
    }
  };

  const fetchCards = async (matchId) => {
    if (cards[matchId]) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/cards/match/${matchId}`
      );
      setCards((prev) => ({
        ...prev,
        [matchId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching cards:", error);
    }
  };

  const fetchPenaltyMisses = async (matchId) => {
    if (penaltyMisses[matchId]) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/penalty-misses/match/${matchId}`
      );
      setPenaltyMisses((prev) => ({
        ...prev,
        [matchId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching penalty misses:", error);
    }
  };

  const fetchMatchStatistics = async (matchId) => {
    await Promise.all([
      fetchShotsOnTarget(matchId),
      fetchSaves(matchId),
      fetchClearances(matchId),
      fetchCards(matchId),
      fetchPenaltyMisses(matchId)
    ]);
  };

  const fetchSubstitutes = async (matchId) => {
    if (substitutes[matchId]) return; // If already fetched, skip
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/match-players/substitutes/${matchId}`
      );
      setSubstitutes((prev) => ({
        ...prev,
        [matchId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching substitutes:", error);
      toast.error("Failed to fetch substitutes.");
    }
  };

  const handleViewStatistics = async (matchId) => {
    // Only fetch if we don't already have the data
    if (!shotsOnTarget[matchId] && !saves[matchId] && !clearances[matchId] && !cards[matchId] && !penaltyMisses[matchId]) {
      await fetchMatchStatistics(matchId);
    }
  };

  const handleViewSubstitutes = async (match) => {
    setShowSubstitutesModal(match);
    await fetchSubstitutes(match.matchesId);
  };

  const handleStatsSubmit = async (e, match) => {
    e.preventDefault();
    
    // Validate required fields
    if (!statsFormData.type || !statsFormData.playerId || !statsFormData.minute) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Additional validation for cards
    if (statsFormData.type === "card" && !statsFormData.cardType) {
      toast.error("Please select a card type");
      return;
    }

    setLoadingStates(prev => ({ ...prev, addScore: true }));
    
    try {
      const payload = {
        playerId: Number(statsFormData.playerId),
        matchId: Number(match.matchesId),
        minute: Number(statsFormData.minute),
      };

      let endpoint = "";
      switch (statsFormData.type) {
        case "shot":
          endpoint = `${process.env.REACT_APP_API_URL}/shots-on-target`;
          break;
        case "save":
          endpoint = `${process.env.REACT_APP_API_URL}/saves`;
          break;
        case "clearance":
          endpoint = `${process.env.REACT_APP_API_URL}/clearances`;
          break;
        case "card":
          endpoint = `${process.env.REACT_APP_API_URL}/cards`;
          payload.cardType = statsFormData.cardType;
          payload.reason = statsFormData.reason || null;
          break;
        case "penalty-miss":
          endpoint = `${process.env.REACT_APP_API_URL}/penalty-misses`;
          break;
        default:
          throw new Error("Invalid statistics type");
      }

      const response = await axios.post(endpoint, payload);
      toast.success(`${statsFormData.type.charAt(0).toUpperCase() + statsFormData.type.slice(1)} added successfully!`);
      
      // Refresh the specific statistics
      await fetchMatchStatistics(match.matchesId);
      
      setShowAddStatsForm(null);
      resetStatsFormData();
    } catch (error) {
      console.error("Error adding statistics:", error.response?.data || error);
      toast.error("Failed to add statistics.");
    } finally {
      setLoadingStates(prev => ({ ...prev, addScore: false }));
    }
  };

  const fetchPlayers = async (teamId) => {
    if (!teamId) return;
    setPlayersLoading(true);
    setPlayersError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/players/club/${teamId}`
      );
      setPlayers(response.data);
    } catch (error) {
      console.error("Error fetching players:", error);
      setPlayers([]);
      setPlayersError("Failed to load players. Please retry.");
    } finally {
      setPlayersLoading(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      timeScored: "",
      playerId: "",
      matchId: "",
      teamId: "",
      isOwnGoal: false,
      isPenalty: false,
      assistPlayerId: "",
    });
    setSelectedTeamId(null);
    setPlayers([]);
  };

  const resetStatsFormData = () => {
    setStatsFormData({
      type: "",
      playerId: "",
      minute: "",
      cardType: "",
      reason: "",
    });
    setSelectedTeamId(null);
    setPlayers([]);
  };

  // Update the Cancel button to reset the form
  <button
    onClick={() => {
      setShowAddScoreForm(null);
      resetFormData();
    }}
    className="bg-surface-700 hover:bg-surface-700 text-white px-4 py-2 rounded"
  >
    Cancel
  </button>;

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Ongoing matches logic
  const isMatchOngoing = (match) => {
    if (!match?.date) return false;
    const matchDate = new Date(match.date);
    const now = currentTime;
    const matchEndTime = new Date(matchDate.getTime() + 120 * 60000); // 120 minutes match duration
    return now >= matchDate && now <= matchEndTime;
  };

  // Add helper function to filter upcoming matches
  const upcomingMatches = useMemo(() => {
    return matches?.filter((match) => {
      if (!match?.date) return false;
      return new Date(match.date) > currentTime;
    });
  }, [matches, currentTime]);

  // Add helper function to filter past matches
  const pastMatches = useMemo(() => {
    return matches?.filter((match) => {
      if (!match?.date) return false;
      return new Date(match.date) < currentTime;
    });
  }, [matches, currentTime]);

  const ongoingMatches = useMemo(() => {
    return matches?.filter((match) => match !== null && isMatchOngoing(match));
  }, [matches, currentTime]);

  const getMinutesPlayed = (matchDate) => {
    const start = new Date(matchDate);
    const diff = currentTime - start;
    return Math.floor(diff / 60000); // Convert ms to minutes
  };

  // Update the handleNewMatchChange function to ensure proper number conversion
  const handleNewMatchChange = (e) => {
    const { name, value } = e.target;
    console.log(`Changing ${name} to:`, value); // Debug log
    
    // Handle different types of inputs
    setNewMatch((prev) => ({
      ...prev,
      [name]: name === 'date' ? value : // Keep date as string
             (name === 'location' ? value : // Keep location as string
             (value === "" ? "" : Number(value))) // Convert others to number if not empty
    }));
  };

  // Update the handleSubmit function to remove score handling
  const handleSubmit = async () => {
    if (!newMatch.sportId || !newMatch.homeTeamId || !newMatch.awayTeamId) {
      toast.error("Please fill in all required fields (Sport, Home Team, Away Team)");
      return;
    }

    if (newMatch.homeTeamId === newMatch.awayTeamId) {
      toast.error("Home team and away team must be different");
      return;
    }

    setLoadingStates(prev => ({ ...prev, addMatch: true }));
    try {
      const matchToAdd = {
        ...newMatch,
        sportId: Number(newMatch.sportId),
        homeTeamId: Number(newMatch.homeTeamId),
        awayTeamId: Number(newMatch.awayTeamId),
        leagueId: Number(newMatch.leagueId),
        date: newMatch.date ? new Date(newMatch.date).toISOString() : null,
        location: newMatch.location ? newMatch.location.trim() : null
      };

      const matchResponse = await addMatch(matchToAdd);
      console.log('addMatch response:', matchResponse);
      const matchId = matchResponse.matchesId;
      if (!matchId) {
        toast.error('Failed to get match ID from server. Cannot assign players.');
        return;
      }

      // Assign players
      const assignments = [
        ...homeStarting11.map(playerId => ({ matchId, playerId, isStarting: true })),
        ...homeSubs.map(playerId => ({ matchId, playerId, isStarting: false })),
        ...awayStarting11.map(playerId => ({ matchId, playerId, isStarting: true })),
        ...awaySubs.map(playerId => ({ matchId, playerId, isStarting: false })),
      ];

      await Promise.all(assignments.map(assignment =>
        axios.post(`${process.env.REACT_APP_API_URL}/match-players/`, assignment)
      ));

      await fetchMatches();
      
      setNewMatch({
        sportId: "",
        date: "",
        location: "",
        homeTeamId: "",
        awayTeamId: "",
        leagueId: "",
      });
      setShowAddForm(false);
      toast.success("Match added successfully!");
    } catch (error) {
      console.error("Error adding match:", error);
      toast.error(`Failed to add match: ${error.message || 'Please try again.'}`);
    } finally {
      setLoadingStates(prev => ({ ...prev, addMatch: false }));
    }
  };

  const handleEditSubmit = async () => {
    if (!editingMatch.sportId || !editingMatch.homeTeamId || !editingMatch.awayTeamId || !editingMatch.date) {
      toast.error("Please fill in all required fields (Sport, Teams, Date)");
      return;
    }

    if (editingMatch.homeTeamId === editingMatch.awayTeamId) {
      toast.error("Home team and away team must be different");
      return;
    }

    setLoadingStates(prev => ({ ...prev, editMatch: true }));
    try {
      const parsedDate = new Date(editingMatch.date);
      if (isNaN(parsedDate.getTime())) {
        toast.error("Please enter a valid date");
        return;
      }

      const updatedMatch = {
        matchesId: editingMatch.matchesId,
        sportId: parseInt(editingMatch.sportId),
        homeTeamId: parseInt(editingMatch.homeTeamId),
        awayTeamId: parseInt(editingMatch.awayTeamId),
        date: parsedDate.toISOString(),
        location: editingMatch.location?.trim() || null,
        homeTeamScore: parseInt(editingMatch.homeTeamScore) || 0,
        awayTeamScore: parseInt(editingMatch.awayTeamScore) || 0,
        leagueId: parseInt(editingMatch.leagueId)
      };

      await updateMatch(updatedMatch);
      await fetchMatches();
      
      setEditingMatch(null);
      toast.success("Match updated successfully!");
    } catch (error) {
      console.error("Error updating match:", error);
      toast.error(`Failed to update match: ${error.message || 'Please try again.'}`);
    } finally {
      setLoadingStates(prev => ({ ...prev, editMatch: false }));
    }
  };

  const cancelEdit = () => {
    setEditingMatch(null);
  };

  const startEdit = (match) => {
    try {
      const formattedDate = format(
        new Date(match?.date || new Date()),
        "yyyy-MM-dd'T'HH:mm"
      );

      setEditingMatch({
        ...match,
        sportId: match?.sportId?.toString() || "",
        homeTeamId: match?.homeTeamId?.toString() || "",
        awayTeamId: match?.awayTeamId?.toString() || "",
        leagueId: match?.leagueId?.toString() || "",
        date: formattedDate,
        location: match?.location || "",
        matchesId: match?.matchesId,
        homeTeamScore: match?.homeTeamScore || 0,
        awayTeamScore: match?.awayTeamScore || 0,
      });
    } catch (error) {
      console.error("Error starting edit:", error);
      toast.error("Error loading match data for editing.");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingMatch((prev) => ({
      ...prev,
      [name]:
        name === "homeTeamScore" || name === "awayTeamScore"
          ? parseInt(value, 10) || 0
          : value,
    }));
  };

  const formatMatchDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MMM d, yyyy HH:mm");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const [formData, setFormData] = useState({
    timeScored: "",
    playerId: "",
    matchId: "",
    teamId: "",
    isOwnGoal: false,
    isPenalty: false,
    assistPlayerId: "",
  });

  const handleScoreChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? checked : value === "" ? null : Number(value), // Convert to number or null
    });
  };

  const handleStatsChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log('Stats form change:', { name, value, type, checked }); // Debug log
    const newFormData = {
      ...statsFormData,
      [name]: type === "checkbox" ? checked : 
              (name === "cardType" || name === "reason" || name === "type") ? value : // Keep strings as strings
              value === "" ? null : Number(value), // Convert others to number or null
    };
    console.log('New form data:', newFormData); // Debug log
    setStatsFormData(newFormData);
  };

  const handleTeamChange = (e, match) => {
    const teamId = e.target.value;
    setSelectedTeamId(teamId);
    if (teamId) {
      fetchPlayers(teamId);
    } else {
      setPlayers([]);
    }
  };

  const handleFullTimeScoreSubmit = async (e) => {
    e.preventDefault();
    if (
      !fullTimeScoreMatch ||
      fullTimeScore.homeTeamScore === "" ||
      fullTimeScore.awayTeamScore === "" ||
      !Number.isInteger(Number(fullTimeScore.homeTeamScore)) ||
      !Number.isInteger(Number(fullTimeScore.awayTeamScore)) ||
      Number(fullTimeScore.homeTeamScore) < 0 ||
      Number(fullTimeScore.awayTeamScore) < 0
    ) {
      toast.error("Enter the full-time score for both teams.");
      return;
    }

    setFullTimeScorePending(true);
    try {
      const updates = {
        matchesId: fullTimeScoreMatch.matchesId,
        homeTeamScore: Number(fullTimeScore.homeTeamScore),
        awayTeamScore: Number(fullTimeScore.awayTeamScore),
      };
      await updateMatch(updates);
      setMatches((prev) => prev.map((match) =>
        match.matchesId === fullTimeScoreMatch.matchesId ? { ...match, ...updates } : match
      ));
      setFullTimeScoreMatch(null);
      toast.success("Full-time score saved.");
    } catch (error) {
      console.error("Error saving full-time score:", error);
      toast.error("Failed to save full-time score.");
    } finally {
      setFullTimeScorePending(false);
    }
  };

  const openFullTimeScoreForm = (match) => {
    setFullTimeScoreMatch(match);
    setFullTimeScore({
      homeTeamScore: match.homeTeamScore == null ? "" : String(match.homeTeamScore),
      awayTeamScore: match.awayTeamScore == null ? "" : String(match.awayTeamScore),
    });
  };

  const getMatchScore = (match, matchScores = []) => {
    if (match.homeTeamScore != null && match.awayTeamScore != null) {
      return { home: match.homeTeamScore, away: match.awayTeamScore };
    }
    return {
      home: matchScores.filter((score) => score.clubId === match.homeTeamId).length,
      away: matchScores.filter((score) => score.clubId === match.awayTeamId).length,
    };
  };

  const handleScoreSubmit = async (e, match) => {
    e.preventDefault();
    setLoadingStates(prev => ({ ...prev, addScore: true }));
    const payload = {
      ...formData,
      matchId: Number(match.matchesId),
      teamId: Number(selectedTeamId),
      playerId: formData.playerId ? Number(formData.playerId) : null,
      timeScored: formData.timeScored || null,
      assistPlayerId: formData.assistPlayerId
        ? Number(formData.assistPlayerId)
        : null,
    };
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/scores/scores`,
        payload
      );
      toast.success("Score added successfully!");
      setShowAddScoreForm(null);
      resetFormData();
      await fetchScores(match.matchesId);
    } catch (error) {
      console.error("Error adding score:", error.response?.data || error);
      toast.error("Failed to add score.");
    } finally {
      setLoadingStates(prev => ({ ...prev, addScore: false }));
    }
  };

  useEffect(() => {
    // Fetch scores for ongoing matches when the component mounts
    const fetchOngoingMatchScores = async () => {
      for (const match of ongoingMatches) {
        await fetchScores(match.matchesId);
      }
    };
    fetchOngoingMatchScores();
  }, [ongoingMatches]);

  // Add useEffect to fetch scores for past matches
  useEffect(() => {
    // Fetch scores for past matches when the component mounts or when activeTab changes to "played"
    const fetchPastMatchScores = async () => {
      if (activeTab === "played") {
        for (const match of pastMatches) {
          if (!scores[match.matchesId]) { // Only fetch if scores aren't already loaded
            await fetchScores(match.matchesId);
          }
        }
      }
    };
    fetchPastMatchScores();
  }, [activeTab, pastMatches]); // Remove scores from dependency array to prevent continuous fetching

  const fetchPlayerName = async (playerId) => {
    if (playerNames[playerId]) return; // If already fetched, skip
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/players/${playerId}`
      );
      setPlayerNames(prev => ({
        ...prev,
        [playerId]: response.data.name
      }));
    } catch (error) {
      console.error(`Error fetching player name for ID ${playerId}:`, error);
      // Set a fallback value if fetch fails
      setPlayerNames(prev => ({
        ...prev,
        [playerId]: `Player #${playerId}`
      }));
    }
  };

  useEffect(() => {
    // Fetch player names for all scores when they're loaded
    const fetchPlayerNames = async () => {
      const newPlayerIds = new Set();
      
      // Collect all unique player IDs that we don't already have
      for (const matchId in scores) {
        const matchScores = scores[matchId];
        for (const score of matchScores) {
          if (score.playerId && !playerNames[score.playerId]) {
            newPlayerIds.add(score.playerId);
          }
          if (score.assistPlayerId && !playerNames[score.assistPlayerId]) {
            newPlayerIds.add(score.assistPlayerId);
          }
        }
      }

      // Only fetch new player names
      for (const playerId of newPlayerIds) {
        await fetchPlayerName(playerId);
      }
    };

    fetchPlayerNames();
  }, [scores, playerNames]); // Include playerNames in dependencies

  const fetchLeagues = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/leagues`);
      setLeagues(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching leagues:", error);
      toast.error("Failed to fetch leagues");
      setLeagues([]);
    }
  };

  useEffect(() => {
    fetchLeagues();
  }, []);

  const fetchMatchDetails = async (matches) => {
    try {
      // Create a Set of unique IDs to fetch
      const sportIds = new Set(matches.map(match => match.sportId));
      const teamIds = new Set([
        ...matches.map(match => match.homeTeamId),
        ...matches.map(match => match.awayTeamId)
      ]);

      // Fetch sports data
      const sportsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/sports`);
      const sportsData = Array.isArray(sportsResponse.data) ? sportsResponse.data : [];
      
      // Fetch teams data
      const teamsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/clubs`);
      const teamsData = Array.isArray(teamsResponse.data) ? teamsResponse.data : [];

      // Combine the data
      const enrichedMatches = matches.map(match => ({
        ...match,
        sport: sportsData.find(sport => sport.sportId === match.sportId),
        homeTeam: teamsData.find(team => team.clubId === match.homeTeamId),
        awayTeam: teamsData.find(team => team.clubId === match.awayTeamId),
        homeTeamScore: match.homeTeamScore,
        awayTeamScore: match.awayTeamScore,
      }));

      console.log('Enriched matches:', enrichedMatches); // Debug log
      return enrichedMatches;
    } catch (error) {
      console.error('Error fetching related data:', error);
      throw error;
    }
  };

  const fetchMatches = async () => {
    setLoadingStates(prev => ({ ...prev, fetchMatches: true }));
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/matches`);
      if (response.data && Array.isArray(response.data)) {
        const enrichedMatches = await fetchMatchDetails(response.data);
        setMatches(enrichedMatches);
        toast.success("Matches updated successfully");
      } else {
        console.error("Invalid matches data format:", response.data);
        toast.error("Invalid matches data received");
      }
    } catch (error) {
      console.error("Error fetching matches:", error);
      toast.error("Failed to fetch matches");
    } finally {
      setLoadingStates(prev => ({ ...prev, fetchMatches: false }));
    }
  };

  // Add this console log to help debug the data structure
  useEffect(() => {
    console.log('Current matches state:', matches);
  }, [matches]);

  // Add loading spinner component
  const LoadingSpinner = () => (
    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  const fetchTeamPlayers = async (teamId, setPlayers) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/players/club/${teamId}`);
      setPlayers(response.data);
    } catch (error) {
      toast.error("Failed to fetch players for team");
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between mb-6">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab("matches")}
            className={`px-4 py-2 rounded-lg transition-all ${
              activeTab === "matches"
                ? "bg-accent shadow-lg shadow-accent/30"
                : "bg-surface-800 hover:bg-surface-700"
            } text-white font-semibold`}
          >
            Upcoming Matches
          </button>
          <button
            onClick={() => setActiveTab("ongoing")}
            className={`px-4 py-2 rounded-lg transition-all ${
              activeTab === "ongoing"
                ? "bg-accent shadow-lg shadow-accent/30"
                : "bg-surface-800 hover:bg-surface-700"
            } text-white font-semibold`}
          >
            Live Matches
          </button>
          <button
            onClick={() => setActiveTab("played")}
            className={`px-4 py-2 rounded-lg transition-all ${
              activeTab === "played"
                ? "bg-accent shadow-lg shadow-accent/30"
                : "bg-surface-800 hover:bg-surface-700"
            } text-white font-semibold`}
          >
            Played Games
          </button>
          <button
            onClick={() => setActiveTab("videos")}
            className={`px-4 py-2 rounded-lg transition-all ${
              activeTab === "videos"
                ? "bg-accent shadow-lg shadow-accent/30"
                : "bg-surface-800 hover:bg-surface-700"
            } text-white font-semibold`}
          >
            Videos
          </button>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={fetchMatches}
            className={`bg-gray-800 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:scale-105 flex items-center ${
              loadingStates.fetchMatches ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={loadingStates.fetchMatches}
          >
            {loadingStates.fetchMatches ? (
              <LoadingSpinner />
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </>
            )}
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className={`bg-accent hover:bg-accent-hover text-white font-semibold py-2 px-4 rounded-lg transition-all hover:scale-105 flex items-center ${
              loadingStates.addMatch ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={loadingStates.addMatch}
          >
            {loadingStates.addMatch ? (
              <>
                <LoadingSpinner />
                <span className="ml-2">Adding...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Match
              </>
            )}
          </button>
        </div>
      </div>

      {/* Add Videos Tab Content */}
      {activeTab === "videos" && (
        <VideosTab matches={matches} />
      )}

      {/* Add Played Games Tab */}
      {activeTab === "played" && (
        <div className="hidden md:block rounded-xl shadow-xl">
          <table className="min-w-full bg-surface-900">
            <thead className="bg-surface-800">
              <tr>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Date & Time
                </th>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Sport
                </th>
                 <th className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  League
                </th>
                 <th className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Teams
                </th>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Score
                </th>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Location
                </th>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {pastMatches?.map((match) => (
                <React.Fragment key={match?.matchId}>
                  <tr className="hover:bg-surface-700 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-white font-mono text-sm">
                        {formatMatchDate(match.date)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className="text-white text-xs font-medium bg-surface-700 px-2 py-0.5 rounded-full max-w-[140px] truncate inline-block">
                          {match?.sport?.name || match?.Sport?.name || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-white text-xs font-medium bg-surface-700 px-2 py-0.5 rounded-full max-w-[120px] truncate inline-block">
                        {leagues.find(l => l.leagueId === match.leagueId)?.name || "N/A"}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap overflow-hidden">
                      <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                        <div className="flex items-center space-x-1.5">
                          <img 
                            src={match.HomeTeam?.logo || match.homeTeam?.logo} 
                            alt={match.HomeTeam?.name || match.homeTeam?.name}
                            className="w-5 h-5 object-contain"
                          />
                          <span className="text-white text-sm max-w-[110px] truncate">{match.HomeTeam?.name || match.homeTeam?.name || "N/A"}</span>
                        </div>
                        <span className="text-accent-soft font-bold mx-1 text-xs">VS</span>
                        <div className="flex items-center space-x-1.5">
                          <img 
                            src={match.AwayTeam?.logo || match.awayTeam?.logo} 
                            alt={match.AwayTeam?.name || match.awayTeam?.name}
                            className="w-5 h-5 object-contain"
                          />
                          <span className="text-white text-sm max-w-[110px] truncate">{match.AwayTeam?.name || match.awayTeam?.name || "N/A"}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1.5">
                        {/* Calculate and display scores */}
                        {(() => {
                          const matchScores = scores[match.matchesId] || [];
                          const { home: homeTeamGoals, away: awayTeamGoals } = getMatchScore(match, matchScores);

                          return (
                            <>
                              <div className="bg-surface-700 px-2 py-0.5 rounded">
                                <span className="text-accent-soft font-bold text-sm">
                                  {homeTeamGoals}
                                </span>
                              </div>
                              <span className="text-accent-soft font-bold text-sm">:</span>
                              <div className="bg-surface-700 px-2 py-0.5 rounded">
                                <span className="text-accent-soft font-bold text-sm">
                                  {awayTeamGoals}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-300 text-xs max-w-[160px] truncate inline-block">
                        {match?.location || "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => {
                            setShowAddScoreForm(match);
                            fetchScores(match.matchesId);
                          }}
                          className={`bg-accent hover:bg-accent-hover text-white px-2 py-1 rounded text-xs flex items-center ${
                            loadingStates.addScore ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={loadingStates.addScore}
                        >
                          {loadingStates.addScore ? (
                            <LoadingSpinner />
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              Add Score
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => openFullTimeScoreForm(match)}
                          className="bg-surface-700 hover:bg-surface-600 text-white px-2 py-1 rounded text-xs"
                        >
                          Full-Time Score
                        </button>
                        <button
                          onClick={() => {
                            setShowAddStatsForm(match);
                          }}
                          className={`bg-accent-dim hover:bg-accent-dim text-accent-soft px-2.5 py-1 rounded-lg text-xs transition-colors flex items-center ${
                            loadingStates.editMatch ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={loadingStates.editMatch}
                        >
                          {loadingStates.editMatch ? (
                            <LoadingSpinner />
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              Add Stats
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteMatch(match)}
                          className={`bg-gray-700/30 hover:bg-gray-700/50 text-gray-300 px-2.5 py-1 rounded-lg text-xs transition-colors flex items-center ${
                            loadingStates.deleteMatch ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={loadingStates.deleteMatch}
                        >
                          {loadingStates.deleteMatch ? (
                            <LoadingSpinner />
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Score Form */}
                  {showAddScoreForm?.matchesId === match.matchesId && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4">
                        <div className="bg-surface-800 p-4 rounded-lg">
                          <form onSubmit={(e) => handleScoreSubmit(e, match)} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                  Team
                                </label>
                                <select
                                  name="teamId"
                                  value={selectedTeamId || ""}
                                  onChange={(e) => {
                                    setSelectedTeamId(e.target.value);
                                    fetchPlayers(e.target.value);
                                  }}
                                  className="w-full bg-surface-700 text-white rounded px-3 py-2"
                                  required
                                >
                                  <option value="">Select Team</option>
                                  <option value={match.homeTeamId}>{match.HomeTeam?.name || match.homeTeam?.name}</option>
                                  <option value={match.awayTeamId}>{match.AwayTeam?.name || match.awayTeam?.name}</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                  Time Scored
                                </label>
                                <input
                                  type="number"
                                  name="timeScored"
                                  value={formData.timeScored ?? ""}
                                  onChange={handleScoreChange}
                                  className="w-full bg-surface-700 text-white rounded px-3 py-2"
                                  placeholder="Minute (optional)"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                  Scorer
                                </label>
                                <select
                                  name="playerId"
                                  value={formData.playerId ?? ""}
                                  onChange={handleScoreChange}
                                  className="w-full bg-surface-700 text-white rounded px-3 py-2"
                                >
                                  <option value="">Select Player</option>
                                  {players.map((player) => (
                                    <option key={player.playerId} value={player.playerId}>
                                      {player.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                  Assist (Optional)
                                </label>
                                <select
                                  name="assistPlayerId"
                                  value={formData.assistPlayerId}
                                  onChange={handleScoreChange}
                                  className="w-full bg-surface-700 text-white rounded px-3 py-2"
                                >
                                  <option value="">Select Player</option>
                                  {players.map((player) => (
                                    <option key={player.playerId} value={player.playerId}>
                                      {player.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4">
                              <label className="flex items-center space-x-2 text-gray-300">
                                <input
                                  type="checkbox"
                                  name="isPenalty"
                                  checked={formData.isPenalty}
                                  onChange={handleScoreChange}
                                  className="rounded"
                                />
                                <span>Penalty</span>
                              </label>
                              <label className="flex items-center space-x-2 text-gray-300">
                                <input
                                  type="checkbox"
                                  name="isOwnGoal"
                                  checked={formData.isOwnGoal}
                                  onChange={handleScoreChange}
                                  className="rounded"
                                />
                                <span>Own Goal</span>
                              </label>
                            </div>
                            <div className="flex justify-end space-x-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowAddScoreForm(null);
                                  resetFormData();
                                }}
                                className="bg-surface-700 hover:bg-surface-700 text-white px-4 py-2 rounded"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded"
                              >
                                Add Score
                              </button>
                            </div>
                          </form>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Scores display */}
                  {scores[match.matchesId]?.length > 0 && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 bg-surface-800">
                        <div className="space-y-4">
                          {/* Home Team Scores */}
                          <div className="mb-4">
                            <h4 className="text-accent-soft font-bold mb-2">
                              {match.HomeTeam?.name || match.homeTeam?.name} Goals
                            </h4>
                            <div className="space-y-1">
                              {scores[match.matchesId]
                                ?.filter((score) => score.clubId === match.homeTeamId)
                                .map((score) => (
                                  <div
                                    key={score.id}
                                    className="text-white text-sm flex items-center space-x-2"
                                  >
                                    {score.timeScored != null && (
                                      <span className="bg-surface-700 px-2 py-1 rounded">
                                        {score.timeScored}'
                                      </span>
                                    )}
                                    <span>{score.playerId ? playerNames[score.playerId] || `Loading...` : "Unattributed goal"}</span>
                                    {score.assistPlayerId && (
                                      <span className="text-gray-400">
                                        (Assist: {playerNames[score.assistPlayerId] || `Loading...`})
                                      </span>
                                    )}
                                    {score.isOwnGoal && (
                                      <span className="text-amber-400">(Own Goal)</span>
                                    )}
                                    {score.isPenalty && (
                                      <span className="text-accent-soft">(Penalty)</span>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </div>
                          {/* Away Team Scores */}
                          <div>
                            <h4 className="text-accent-soft font-bold mb-2">
                              {match.AwayTeam?.name || match.awayTeam?.name} Goals
                            </h4>
                            <div className="space-y-1">
                              {scores[match.matchesId]
                                ?.filter((score) => score.clubId === match.awayTeamId)
                                .map((score) => (
                                  <div
                                    key={score.id}
                                    className="text-white text-sm flex items-center space-x-2"
                                  >
                                    {score.timeScored != null && (
                                      <span className="bg-surface-700 px-2 py-1 rounded">
                                        {score.timeScored}'
                                      </span>
                                    )}
                                    <span>{score.playerId ? playerNames[score.playerId] || `Loading...` : "Unattributed goal"}</span>
                                    {score.assistPlayerId && (
                                      <span className="text-gray-400">
                                        (Assist: {playerNames[score.assistPlayerId] || `Loading...`})
                                      </span>
                                    )}
                                    {score.isOwnGoal && (
                                      <span className="text-amber-400">(Own Goal)</span>
                                    )}
                                    {score.isPenalty && (
                                      <span className="text-accent-soft">(Penalty)</span>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Statistics display */}
                  {(shotsOnTarget[match.matchesId]?.length > 0 || 
                    saves[match.matchesId]?.length > 0 || 
                    clearances[match.matchesId]?.length > 0 || 
                    cards[match.matchesId]?.length > 0 ||
                    penaltyMisses[match.matchesId]?.length > 0) && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 bg-surface-700">
                        <div className="space-y-4">
                          <h4 className="text-accent-soft font-bold mb-3">Match Statistics</h4>
                          
                          {/* Shots on Target */}
                          {shotsOnTarget[match.matchesId]?.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-amber-400 font-semibold mb-2">Shots on Target</h5>
                              <div className="space-y-1">
                                {shotsOnTarget[match.matchesId].map((shot) => (
                                  <div key={shot.id} className="text-white text-sm flex items-center space-x-2">
                                    <span className="bg-surface-700 px-2 py-1 rounded">
                                      {shot.minute}'
                                    </span>
                                    <span>{playerNames[shot.playerId] || `Player #${shot.playerId}`}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Saves */}
                          {saves[match.matchesId]?.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-emerald-400 font-semibold mb-2">Saves</h5>
                              <div className="space-y-1">
                                {saves[match.matchesId].map((save) => (
                                  <div key={save.id} className="text-white text-sm flex items-center space-x-2">
                                    <span className="bg-surface-700 px-2 py-1 rounded">
                                      {save.minute}'
                                    </span>
                                    <span>{playerNames[save.playerId] || `Player #${save.playerId}`}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Clearances */}
                          {clearances[match.matchesId]?.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-accent-soft font-semibold mb-2">Clearances</h5>
                              <div className="space-y-1">
                                {clearances[match.matchesId].map((clearance) => (
                                  <div key={clearance.id} className="text-white text-sm flex items-center space-x-2">
                                    <span className="bg-surface-700 px-2 py-1 rounded">
                                      {clearance.minute}'
                                    </span>
                                    <span>{playerNames[clearance.playerId] || `${clearance.Player.name}`}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Cards */}
                          {cards[match.matchesId]?.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-orange-400 font-semibold mb-2">Cards</h5>
                              <div className="space-y-1">
                                {cards[match.matchesId].map((card) => (
                                  <div key={card.id} className="text-white text-sm flex items-center space-x-2">
                                    <span className="bg-surface-700 px-2 py-1 rounded">
                                      {card.minute}'
                                    </span>
                                    <span className={`px-2 py-1 rounded ${
                                      card.cardType === 'red' ? 'bg-accent' : 'bg-amber-500'
                                    }`}>
                                      {card.cardType.toUpperCase()}
                                    </span>
                                    <span>{playerNames[card.playerId] || `Player #${card.playerId}`}</span>
                                    {card.reason && (
                                      <span className="text-gray-400">({card.reason})</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Penalty Misses */}
                          {penaltyMisses[match.matchesId]?.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-accent-soft font-semibold mb-2">Penalty Misses</h5>
                              <div className="space-y-1">
                                {penaltyMisses[match.matchesId].map((penaltyMiss) => (
                                  <div key={penaltyMiss.id} className="text-white text-sm flex items-center space-x-2">
                                    <span className="bg-surface-700 px-2 py-1 rounded">
                                      {penaltyMiss.minute}'
                                    </span>
                                    <span>{playerNames[penaltyMiss.playerId] || `Player #${penaltyMiss.playerId}`}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* View Statistics Button (when no statistics are shown) */}
                  {(!shotsOnTarget[match.matchesId]?.length && 
                    !saves[match.matchesId]?.length && 
                    !clearances[match.matchesId]?.length && 
                    !cards[match.matchesId]?.length &&
                    !penaltyMisses[match.matchesId]?.length) && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 bg-surface-700">
                        <button
                          onClick={() => handleViewStatistics(match.matchesId)}
                          className="bg-accent-dim hover:bg-accent-dim text-accent-soft px-4 py-2 rounded-lg transition-colors flex items-center mx-auto"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          View Statistics
                        </button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {pastMatches?.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-16 h-16 mb-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      No played matches found
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Played Games - Mobile Cards */}
      {activeTab === "played" && (
        <div className="md:hidden p-2 space-y-3">
          {pastMatches?.map((match) => {
            const matchScores = scores[match.matchesId] || [];
            const { home: homeTeamGoals, away: awayTeamGoals } = getMatchScore(match, matchScores);
            return (
              <div key={match?.matchesId || match?.matchId} className="bg-gray-800/60 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-300">{formatMatchDate(match.date)}</div>
                  <div className="text-xs bg-surface-700 px-2 py-0.5 rounded-full">{match?.sport?.name || match?.Sport?.name || 'N/A'}</div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <img src={match.HomeTeam?.logo || match.homeTeam?.logo} alt={match.HomeTeam?.name || match.homeTeam?.name} className="w-5 h-5 object-contain" />
                    <span className="text-white text-sm">{match.HomeTeam?.name || match.homeTeam?.name || 'N/A'}</span>
                  </div>
                  <div className="text-accent-soft font-bold">{homeTeamGoals} : {awayTeamGoals}</div>
                  <div className="flex items-center space-x-2">
                    <img src={match.AwayTeam?.logo || match.awayTeam?.logo} alt={match.AwayTeam?.name || match.awayTeam?.name} className="w-5 h-5 object-contain" />
                    <span className="text-white text-sm">{match.AwayTeam?.name || match.awayTeam?.name || 'N/A'}</span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400">{leagues.find(l => l.leagueId === match.leagueId)?.name || 'N/A'} • {match?.location || 'N/A'}</div>
                <div className="mt-3 flex items-center space-x-2 overflow-x-auto whitespace-nowrap">
                  <button
                    onClick={() => { setShowAddScoreForm(match); fetchScores(match.matchesId); }}
                    className={`bg-accent hover:bg-accent-hover text-white px-3 py-1 rounded text-sm ${loadingStates.addScore ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={loadingStates.addScore}
                  >
                    {loadingStates.addScore ? '...' : 'Add Score'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openFullTimeScoreForm(match)}
                    className="bg-surface-700 hover:bg-surface-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Full-Time Score
                  </button>
                  <button
                    onClick={() => { setShowAddStatsForm(match); }}
                    className={`bg-accent-dim hover:bg-accent-dim text-accent-soft px-3 py-1 rounded text-sm ${loadingStates.editMatch ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={loadingStates.editMatch}
                  >
                    {loadingStates.editMatch ? '...' : 'Add Stats'}
                  </button>
                  <button
                    onClick={() => handleDeleteMatch(match)}
                    className={`bg-surface-700 hover:bg-gray-700/50 text-gray-300 px-3 py-1 rounded text-sm ${loadingStates.deleteMatch ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={loadingStates.deleteMatch}
                  >
                    {loadingStates.deleteMatch ? '...' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Live Matches Tab */}
      {activeTab === "ongoing" && (
        <div className="overflow-x-auto rounded-xl shadow-xl">
          <table className="min-w-full bg-surface-900">
            <thead className="bg-surface-800">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Sport
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  League
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Teams
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {ongoingMatches?.map((match) => (
                <tr
                  key={match.matchesId}
                  className="hover:bg-surface-700 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <span className="animate-pulse w-2 h-2 bg-accent rounded-full mr-2"></span>
                      <span className="text-white font-mono text-lg">
                        {getMinutesPlayed(match.date)}'
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <span className="text-white text-sm font-medium bg-surface-700 px-3 py-1 rounded-full">
                        {match?.sport?.name || match?.Sport?.name || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-white text-sm font-medium bg-surface-700 px-3 py-1 rounded-full">
                      {leagues.find(l => l.leagueId === match.leagueId)?.name || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-3">
                        <span className="text-white font-medium">
                          {match?.homeTeam?.name || match?.HomeTeam?.name || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-white font-medium">
                          {match?.awayTeam?.name || match?.AwayTeam?.name || "N/A"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {/* Calculate total goals for home and away teams */}
                      {(() => {
                        // Get scores for the current match
                        const matchScores = scores[match.matchesId] || [];

                        // Calculate total goals for each club
                        const { home: homeTeamGoals, away: awayTeamGoals } = getMatchScore(match, matchScores);

                        return (
                          <>
                            {/* Home Team Goals */}
                            <div className="bg-surface-700 px-3 py-1 rounded">
                              <span className="text-accent-soft font-bold text-lg">
                                {homeTeamGoals}
                              </span>
                            </div>

                            {/* Colon Separator */}
                            <span className="text-accent-soft font-bold text-lg">
                              :
                            </span>

                            {/* Away Team Goals */}
                            <div className="bg-surface-700 px-3 py-1 rounded">
                              <span className="text-accent-soft font-bold text-lg">
                                {awayTeamGoals}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-300 text-sm">
                      {match?.location || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          // startEdit(match);
                          setShowAddScoreForm(match);
                          fetchScores(match.matchesId);
                        }} //Open the form when edit is clicked
                        className={`bg-accent-dim hover:bg-accent-dim text-accent-soft px-4 py-2 rounded-lg transition-colors flex items-center ${
                          loadingStates.editMatch ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        disabled={loadingStates.editMatch}
                      >
                        {loadingStates.editMatch ? (
                          <LoadingSpinner />
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                            Edit
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => openFullTimeScoreForm(match)}
                        className="bg-surface-700 hover:bg-surface-600 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        Full-Time Score
                      </button>
                      <button
                        onClick={() => handleViewSubstitutes(match)}
                        className="bg-emerald-900/40 hover:bg-emerald-900/60 text-emerald-300 px-4 py-2 rounded-lg transition-colors flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Substitutes
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {ongoingMatches?.length === 0 && (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-16 h-16 mb-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      No live matches currently
                    </div>
                  </td>
                  {ongoingMatches?.map((match) => (
                    <React.Fragment key={match.matchId}>
                      {/* Existing match row */}
                      <tr className="hover:bg-surface-700 transition-colors">
                        {/* ... existing columns ... */}
                        <td className="px-6 py-4">
                          <button
                            onClick={() => {
                              setShowAddScoreForm(match);
                              fetchScores(match.matchesId); // Fetch scores when opening
                            }}
                          >
                            Edit Score
                          </button>
                        </td>
                      </tr>

                      {/* Scores display */}
                      {showAddScoreForm?.matchesId === match.matchesId && (
                        <tr>
                          <td colSpan="6" className="p-4">
                            <div className="card p-4">
                              {/* Home Team Scores */}
                              <div className="mb-6">
                                <h3 className="text-accent-soft font-bold mb-2">
                                  {match.HomeTeam?.name || match.homeTeam?.name} Goals
                                </h3>
                                <table className="w-full">
                                  <thead>
                                    <tr>
                                      <th className="text-left text-gray-300">
                                        Time
                                      </th>
                                      <th className="text-left text-gray-300">
                                        Scorer
                                      </th>
                                      <th className="text-left text-gray-300">
                                        Assist
                                      </th>
                                      <th className="text-left text-gray-300">
                                        Type
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {scores[match.matchesId]
                                      ?.filter(
                                        (score) =>
                                          score.clubId === match.homeTeamId
                                      )
                                      .map((score) => (
                                        <tr
                                          key={score.id}
                                          className="text-white"
                                        >
                                          <td>{score.timeScored != null ? `${score.timeScored}'` : "-"}</td>
                                          <td>{score.playerId ? `Player #${score.playerId}` : "Unattributed goal"}</td>
                                          <td>
                                            {score.assistPlayerId
                                              ? `Player #${score.assistPlayerId}`
                                              : "-"}
                                          </td>
                                          <td>
                                            {score.isOwnGoal
                                              ? "Own Goal"
                                              : score.isPenalty
                                              ? "Penalty"
                                              : "Goal"}
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>

                              {/* Away Team Scores */}
                              <div className="mb-6">
                                <h3 className="text-accent-soft font-bold mb-2">
                                  {match.AwayTeam?.name || match.awayTeam?.name} Goals
                                </h3>
                                <table className="w-full">
                                  <thead>
                                    <tr>
                                      <th className="text-left text-gray-300">
                                        Time
                                      </th>
                                      <th className="text-left text-gray-300">
                                        Scorer
                                      </th>
                                      <th className="text-left text-gray-300">
                                        Assist
                                      </th>
                                      <th className="text-left text-gray-300">
                                        Type
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {scores[match.matchesId]
                                      ?.filter(
                                        (score) =>
                                          score.clubId === match.awayTeamId
                                      )
                                      .map((score) => (
                                        <tr
                                          key={score.id}
                                          className="text-white"
                                        >
                                          <td>{score.timeScored != null ? `${score.timeScored}'` : "-"}</td>
                                          <td>{score.playerId ? `Player #${score.playerId}` : "Unattributed goal"}</td>
                                          <td>
                                            {score.assistPlayerId
                                              ? `Player #${score.assistPlayerId}`
                                              : "-"}
                                          </td>
                                          <td>
                                            {score.isOwnGoal
                                              ? "Own Goal"
                                              : score.isPenalty
                                              ? "Penalty"
                                              : "Goal"}
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Upcoming Matches Tab */}
      {activeTab === "matches" && (
        <div className="overflow-x-auto rounded-xl shadow-xl">
          <table className="min-w-full bg-surface-900">
            <thead className="bg-surface-800">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Sport
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  League
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Matchup
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {upcomingMatches?.map((match) => (
                <tr
                  key={match?.matchId}
                  className="hover:bg-surface-700 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="text-white text-sm">
                      {formatMatchDate(match?.date || "")}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-white text-sm font-medium bg-surface-700 px-3 py-1 rounded-full">
                      {match?.sport?.name || match?.Sport?.name || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-white text-sm font-medium bg-surface-700 px-3 py-1 rounded-full">
                      {leagues.find(l => l.leagueId === match.leagueId)?.name || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-white font-medium">
                        {match?.homeTeam?.name || match?.HomeTeam?.name || "N/A"}
                      </span>
                      <span className="text-gray-400">vs</span>
                      <span className="text-white font-medium">
                        {match?.awayTeam?.name || match?.AwayTeam?.name || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-300 text-sm">
                      {match?.location || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => startEdit(match)}
                        className={`bg-accent-dim hover:bg-accent-dim text-accent-soft px-3 py-1 rounded-lg transition-colors flex items-center ${
                          loadingStates.editMatch ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        disabled={loadingStates.editMatch}
                      >
                        {loadingStates.editMatch ? (
                          <LoadingSpinner />
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                            Edit
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteMatch(match)}
                        className={`bg-gray-700/30 hover:bg-gray-700/50 text-gray-300 px-3 py-1 rounded-lg transition-colors flex items-center ${
                          loadingStates.deleteMatch ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        disabled={loadingStates.deleteMatch}
                      >
                        {loadingStates.deleteMatch ? (
                          <LoadingSpinner />
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {upcomingMatches?.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-16 h-16 mb-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      No upcoming matches found
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Match Modal */}
      {editingMatch && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-surface-900 rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Edit Match</h3>
              <button
                onClick={cancelEdit}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2 space-y-4">
              <div className="space-y-4">
                <div className="bg-surface-800 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sport
                  </label>
                  <select
                    name="sportId"
                    value={editingMatch.sportId}
                    onChange={handleEditChange}
                    className="w-full bg-surface-700 text-white px-4 py-3 rounded-lg focus:ring-2 focus:ring-accent outline-none"
                  >
                    <option value="">Select Sport</option>
                    {sports.map((sport) => (
                      <option key={sport.sportId} value={sport.sportId}>
                        {sport.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-surface-800 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Home Team
                  </label>
                  <select
                    name="homeTeamId"
                    value={editingMatch.homeTeamId}
                    onChange={handleEditChange}
                    className="w-full bg-surface-700 text-white px-4 py-3 rounded-lg focus:ring-2 focus:ring-accent outline-none"
                  >
                    <option value="">Select Home Team</option>
                    {homeTeams?.map((team) => (
                      <option key={team.clubId} value={team.clubId}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-surface-800 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Away Team
                  </label>
                  <select
                    name="awayTeamId"
                    value={editingMatch.awayTeamId}
                    onChange={handleEditChange}
                    className="w-full bg-surface-700 text-white px-4 py-3 rounded-lg focus:ring-2 focus:ring-accent outline-none"
                  >
                    <option value="">Select Away Team</option>
                    {awayTeams?.map((team) => (
                      <option key={team.clubId} value={team.clubId}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-surface-800 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    name="date"
                    value={editingMatch.date}
                    onChange={handleEditChange}
                    className="w-full bg-surface-700 text-white px-4 py-3 rounded-lg focus:ring-2 focus:ring-accent outline-none"
                  />
                </div>

                <div className="bg-surface-800 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={editingMatch.location}
                    onChange={handleEditChange}
                    className="w-full bg-surface-700 text-white px-4 py-3 rounded-lg focus:ring-2 focus:ring-accent outline-none"
                    placeholder="Enter location"
                  />
                </div>

                <div className="bg-surface-800 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    League
                  </label>
                  <select
                    name="leagueId"
                    value={editingMatch.leagueId}
                    onChange={handleEditChange}
                    className="w-full bg-surface-700 text-white px-4 py-3 rounded-lg focus:ring-2 focus:ring-accent outline-none"
                  >
                    <option value="">Select League</option>
                    {leagues.map((league) => (
                      <option key={league.leagueId} value={league.leagueId}>
                        {league.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-line">
              <button
                onClick={handleEditSubmit}
                className="bg-accent hover:bg-accent-hover text-white px-6 py-2 rounded-lg transition-all flex items-center"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save Changes
              </button>
              <button
                onClick={cancelEdit}
                className="bg-surface-700 hover:bg-surface-700 text-white px-6 py-2 rounded-lg transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Match Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-surface-900 rounded-2xl p-8 w-full max-w-3xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Add New Match</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2 space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Date and Time
                  </label>
                  <input
                    type="datetime-local"
                    name="date"
                    value={newMatch.date}
                    onChange={handleNewMatchChange}
                    className="w-full bg-surface-800 text-white rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    League
                  </label>
                  <select
                    name="leagueId"
                    value={newMatch.leagueId}
                    onChange={handleNewMatchChange}
                    className="w-full bg-surface-800 text-white rounded px-3 py-2"
                  >
                    <option value="">Select League</option>
                    {leagues.map((league) => (
                      <option key={league.leagueId} value={league.leagueId}>
                        {league.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Sport
                  </label>
                  <select
                    name="sportId"
                    value={newMatch.sportId}
                    onChange={handleNewMatchChange}
                    className="w-full bg-surface-800 text-white rounded px-3 py-2"
                  >
                    <option value="">Select Sport</option>
                    {sports.map((sport) => (
                      <option key={sport.sportId} value={sport.sportId}>
                        {sport.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Home Team
                  </label>
                  <select
                    name="homeTeamId"
                    value={newMatch.homeTeamId}
                    onChange={async (e) => {
                      handleNewMatchChange(e);
                      setHomeStarting11([]);
                      setHomeSubs([]);
                      if (e.target.value) {
                        await fetchTeamPlayers(e.target.value, setHomePlayers);
                      } else {
                        setHomePlayers([]);
                      }
                    }}
                    className="w-full bg-surface-800 text-white rounded px-3 py-2"
                  >
                    <option value="">Select Home Team</option>
                    {homeTeams?.map((team) => (
                      <option key={team.clubId} value={team.clubId}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Away Team
                  </label>
                  <select
                    name="awayTeamId"
                    value={newMatch.awayTeamId}
                    onChange={async (e) => {
                      handleNewMatchChange(e);
                      setAwayStarting11([]);
                      setAwaySubs([]);
                      if (e.target.value) {
                        await fetchTeamPlayers(e.target.value, setAwayPlayers);
                      } else {
                        setAwayPlayers([]);
                      }
                    }}
                    className="w-full bg-surface-800 text-white rounded px-3 py-2"
                  >
                    <option value="">Select Away Team</option>
                    {awayTeams?.map((team) => (
                      <option key={team.clubId} value={team.clubId}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={newMatch.location}
                    onChange={handleNewMatchChange}
                    className="w-full bg-surface-800 text-white rounded px-3 py-2"
                    placeholder="Enter location"
                  />
                </div>
                {/* Home Team Starting 11 and Subs Dual-List UI */}
                {newMatch.homeTeamId && (
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-white mb-2">
                      Home Team Starting 11 <span className="text-xs text-gray-400">(Select 11)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {homePlayers.map(player => {
                        const isSelected = homeStarting11.includes(player.playerId);
                        const isDisabled = !isSelected && homeStarting11.length >= 11;
                        return (
                          <button
                            key={player.playerId}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setHomeStarting11(homeStarting11.filter(id => id !== player.playerId));
                              } else if (!isDisabled) {
                                setHomeStarting11([...homeStarting11, player.playerId]);
                                setHomeSubs(homeSubs.filter(id => id !== player.playerId));
                              }
                            }}
                            className={`px-3 py-1 rounded-lg border transition-all
                              ${isSelected ? "bg-accent text-white border-accent-hover" : "bg-surface-800 text-gray-200 border-line-strong"}
                              ${isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-accent-hover"}
                            `}
                            disabled={isDisabled}
                          >
                            {player.name}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Selected: {homeStarting11.length}
                    </div>
                    {/* Subs */}
                    <label className="block text-sm font-bold text-white mt-4 mb-2">
                      Home Team Subs <span className="text-xs text-gray-400">(Optional)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {homePlayers
                        .filter(player => !homeStarting11.includes(player.playerId))
                        .map(player => {
                          const isSelected = homeSubs.includes(player.playerId);
                          return (
                            <button
                              key={player.playerId}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setHomeSubs(homeSubs.filter(id => id !== player.playerId));
                                } else {
                                  setHomeSubs([...homeSubs, player.playerId]);
                                }
                              }}
                              className={`px-3 py-1 rounded-lg border transition-all
                                ${isSelected ? "bg-accent text-white border-accent-hover" : "bg-surface-800 text-gray-200 border-line-strong"}
                                hover:bg-accent-hover
                              `}
                            >
                              {player.name}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}
                {/* Away Team Starting 11 and Subs Dual-List UI */}
                {newMatch.awayTeamId && (
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-white mb-2">
                      Away Team Starting 11 <span className="text-xs text-gray-400">(Select 11)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {awayPlayers.map(player => {
                        const isSelected = awayStarting11.includes(player.playerId);
                        const isDisabled = !isSelected && awayStarting11.length >= 11;
                        return (
                          <button
                            key={player.playerId}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setAwayStarting11(awayStarting11.filter(id => id !== player.playerId));
                              } else if (!isDisabled) {
                                setAwayStarting11([...awayStarting11, player.playerId]);
                                setAwaySubs(awaySubs.filter(id => id !== player.playerId));
                              }
                            }}
                            className={`px-3 py-1 rounded-lg border transition-all
                              ${isSelected ? "bg-accent text-white border-accent-hover" : "bg-surface-800 text-gray-200 border-line-strong"}
                              ${isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-accent-hover"}
                            `}
                            disabled={isDisabled}
                          >
                            {player.name}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Selected: {awayStarting11.length}
                    </div>
                    {/* Subs */}
                    <label className="block text-sm font-bold text-white mt-4 mb-2">
                      Away Team Subs <span className="text-xs text-gray-400">(Optional)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {awayPlayers
                        .filter(player => !awayStarting11.includes(player.playerId))
                        .map(player => {
                          const isSelected = awaySubs.includes(player.playerId);
                          return (
                            <button
                              key={player.playerId}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setAwaySubs(awaySubs.filter(id => id !== player.playerId));
                                } else {
                                  setAwaySubs([...awaySubs, player.playerId]);
                                }
                              }}
                              className={`px-3 py-1 rounded-lg border transition-all
                                ${isSelected ? "bg-accent text-white border-accent-hover" : "bg-surface-800 text-gray-200 border-line-strong"}
                                hover:bg-accent-hover
                              `}
                            >
                              {player.name}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-line">
              <button
                onClick={handleSubmit}
                className={`bg-accent hover:bg-accent-hover text-white px-6 py-2 rounded-lg transition-all flex items-center ${
                  loadingStates.addMatch ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={loadingStates.addMatch}
              >
                {loadingStates.addMatch ? (
                  <>
                    <LoadingSpinner />
                    <span className="ml-2">Adding...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Add Match
                  </>
                )}
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="bg-surface-700 hover:bg-surface-700 text-white px-6 py-2 rounded-lg transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Score Modal */}
      {showAddScoreForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-surface-900 rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Add Score</h3>
              <button
                onClick={() => {
                  setShowAddScoreForm(null);
                  resetFormData();
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={(e) => handleScoreSubmit(e, showAddScoreForm)} className="space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Select Team
                  </label>
                  <select
                    name="teamId"
                    value={formData.teamId}
                     onChange={async (e) => {
                       const newTeamId = e.target.value;
                       setSelectedTeamId(newTeamId);
                       handleScoreChange(e);
                       await fetchPlayersForScorer(showAddScoreForm, newTeamId, formData.isOwnGoal);
                     }}
                     className="w-full bg-surface-800 text-white rounded px-3 py-2 disabled:opacity-60"
                     disabled={playersLoading}
                  >
                    <option value="">Select Team</option>
                    <option value={showAddScoreForm.homeTeamId}>
                      {showAddScoreForm.HomeTeam?.name || showAddScoreForm.homeTeam?.name}
                    </option>
                    <option value={showAddScoreForm.awayTeamId}>
                      {showAddScoreForm.AwayTeam?.name || showAddScoreForm.awayTeam?.name}
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Select Scorer
                  </label>
                  <select
                    name="playerId"
                    value={formData.playerId ?? ""}
                    onChange={handleScoreChange}
                     className="w-full bg-surface-800 text-white rounded px-3 py-2 disabled:opacity-60"
                     disabled={!selectedTeamId || playersLoading}
                  >
                    <option value="">Select Scorer</option>
                    {players.map((player) => (
                      <option
                        key={player.playerId}
                        value={Number(player.playerId)}
                      >
                        {" "}
                        {/* Ensure value is a number */}
                        {player.name}
                      </option>
                    ))}
                  </select>
                   {playersLoading && (
                     <div className="mt-2 text-gray-300 flex items-center gap-2">
                       <LoadingSpinner />
                       <span className="text-sm">Loading players…</span>
                     </div>
                   )}
                   {playersError && (
                     <div className="mt-2 text-accent-soft text-sm flex items-center gap-2">
                       <span>{playersError}</span>
                       <button
                         type="button"
                         className="underline"
                         onClick={() => fetchPlayers(selectedTeamId)}
                       >
                         Retry
                       </button>
                     </div>
                   )}
                  {!playersLoading && !playersError && selectedTeamId && players.length === 0 && (
                    <div className="mt-2 text-gray-400 text-sm">
                      No players found for the selected team. Make sure you have added players to the team.
                      <button
                        type="button"
                        className="ml-2 underline"
                        onClick={() => fetchPlayers(selectedTeamId)}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Time Scored
                  </label>
                  <input
                    type="number"
                    name="timeScored"
                    value={formData.timeScored ?? ""}
                    onChange={handleScoreChange}
                    className="w-full bg-surface-800 text-white rounded px-3 py-2"
                    placeholder="Enter time scored (optional, e.g., 45)"
                    min="0"
                    max="120"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Select Assist Player (Optional)
                  </label>
                  <select
                    name="assistPlayerId"
                    value={formData.assistPlayerId}
                    onChange={handleScoreChange}
                     className="w-full bg-surface-800 text-white rounded px-3 py-2 disabled:opacity-60"
                     disabled={!selectedTeamId || playersLoading}
                  >
                    <option value="">Select Assist Player</option>
                    {players.map((player) => (
                      <option
                        key={player.playerId}
                        value={Number(player.playerId)}
                      >
                        {" "}
                        {/* Ensure value is a number */}
                        {player.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <input
                    type="checkbox"
                    name="isOwnGoal"
                    id="isOwnGoal"
                    checked={formData.isOwnGoal}
                     onChange={async (e) => {
                       handleScoreChange(e);
                       await fetchPlayersForScorer(showAddScoreForm, selectedTeamId, e.target.checked);
                     }}
                    className="mr-2"
                  />
                  <label htmlFor="isOwnGoal" className="text-white">
                    Own Goal
                  </label>
                   <div className="text-xs text-gray-400 mt-1">
                     {formData.isOwnGoal
                       ? 'Selecting scorer from conceding team; goal counts for the other team.'
                       : 'Selecting scorer from the chosen team.'}
                   </div>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="isPenalty"
                    id="isPenalty"
                    checked={formData.isPenalty}
                    onChange={handleScoreChange}
                    className="mr-2"
                  />
                  <label htmlFor="isPenalty" className="text-white">
                    Penalty
                  </label>
                </div>

                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    type="submit"
                     className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded disabled:opacity-60"
                     disabled={playersLoading}
                  >
                    Add Score
                  </button>
                  <button
                    onClick={() => {
                      setShowAddScoreForm(null);
                      resetFormData();
                    }}
                    className="bg-surface-700 hover:bg-surface-700 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enter or update the full-time score without adding individual goal events. */}
      {fullTimeScoreMatch && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-surface-900 rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-xl font-bold text-white mb-5">Full-Time Score</h3>
            <form onSubmit={handleFullTimeScoreSubmit} className="space-y-4">
              <label className="block text-sm text-gray-300">
                {fullTimeScoreMatch.HomeTeam?.name || fullTimeScoreMatch.homeTeam?.name || "Home team"}
                <input
                  type="number"
                  min="0"
                  step="1"
                  required
                  value={fullTimeScore.homeTeamScore}
                  onChange={(event) => setFullTimeScore((prev) => ({ ...prev, homeTeamScore: event.target.value }))}
                  className="mt-1 w-full bg-surface-800 text-white rounded px-3 py-2"
                  aria-label="Home team full-time score"
                />
              </label>
              <label className="block text-sm text-gray-300">
                {fullTimeScoreMatch.AwayTeam?.name || fullTimeScoreMatch.awayTeam?.name || "Away team"}
                <input
                  type="number"
                  min="0"
                  step="1"
                  required
                  value={fullTimeScore.awayTeamScore}
                  onChange={(event) => setFullTimeScore((prev) => ({ ...prev, awayTeamScore: event.target.value }))}
                  className="mt-1 w-full bg-surface-800 text-white rounded px-3 py-2"
                  aria-label="Away team full-time score"
                />
              </label>
              <p className="text-xs text-gray-400">Enter the final team scores directly; scorer and goal-minute details are not needed.</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setFullTimeScoreMatch(null)} className="bg-surface-700 text-white px-4 py-2 rounded">
                  Cancel
                </button>
                <button type="submit" disabled={fullTimeScorePending} className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded disabled:opacity-60">
                  {fullTimeScorePending ? "Saving..." : "Save Score"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Statistics Modal */}
      {showAddStatsForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-surface-900 rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Add Statistics</h3>
              <button
                onClick={() => {
                  setShowAddStatsForm(null);
                  resetStatsFormData();
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={(e) => handleStatsSubmit(e, showAddStatsForm)} className="space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Statistics Type
                  </label>
                  <select
                    name="type"
                    value={statsFormData.type}
                    onChange={handleStatsChange}
                    className="w-full bg-surface-800 text-white rounded px-3 py-2"
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="shot">Shot on Target</option>
                    <option value="save">Save</option>
                    <option value="clearance">Clearance</option>
                    <option value="card">Card</option>
                    <option value="penalty-miss">Penalty Miss</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Select Team
                  </label>
                  <select
                    name="teamId"
                    value={selectedTeamId || ""}
                    onChange={(e) => handleTeamChange(e, showAddStatsForm)}
                     className="w-full bg-surface-800 text-white rounded px-3 py-2 disabled:opacity-60"
                    required
                     disabled={playersLoading}
                  >
                    <option value="">Select Team</option>
                    <option value={showAddStatsForm.homeTeamId}>
                      {showAddStatsForm.HomeTeam?.name || showAddStatsForm.homeTeam?.name}
                    </option>
                    <option value={showAddStatsForm.awayTeamId}>
                      {showAddStatsForm.AwayTeam?.name || showAddStatsForm.awayTeam?.name}
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Select Player
                  </label>
                  <select
                    name="playerId"
                    value={statsFormData.playerId || ""}
                    onChange={handleStatsChange}
                     className="w-full bg-surface-800 text-white rounded px-3 py-2 disabled:opacity-60"
                     disabled={!selectedTeamId || playersLoading}
                    required
                  >
                    <option value="">Select Player</option>
                    {players.map((player) => (
                      <option key={player.playerId} value={player.playerId}>
                        {player.name}
                      </option>
                    ))}
                  </select>
                </div>
                 {playersLoading && (
                   <div className="mt-2 text-gray-300 flex items-center gap-2">
                     <LoadingSpinner />
                     <span className="text-sm">Loading players…</span>
                   </div>
                 )}
                 {playersError && (
                   <div className="mt-2 text-accent-soft text-sm flex items-center gap-2">
                     <span>{playersError}</span>
                     <button
                       type="button"
                       className="underline"
                       onClick={() => fetchPlayers(selectedTeamId)}
                     >
                       Retry
                     </button>
                   </div>
                 )}
                 {!playersLoading && !playersError && selectedTeamId && players.length === 0 && (
                   <div className="mt-2 text-gray-400 text-sm">
                     No players found for the selected team.
                     <button
                       type="button"
                       className="ml-2 underline"
                       onClick={() => fetchPlayers(selectedTeamId)}
                     >
                       Retry
                     </button>
                   </div>
                 )}

                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Minute
                  </label>
                  <input
                    type="number"
                    name="minute"
                    value={statsFormData.minute || ""}
                    onChange={handleStatsChange}
                    className="w-full bg-surface-800 text-white rounded px-3 py-2"
                    placeholder="Enter minute (e.g., 45)"
                    min="0"
                    max="120"
                    required
                  />
                </div>

                {/* Card-specific fields */}
                {statsFormData.type === "card" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">
                        Card Type
                      </label>
                      <select
                        name="cardType"
                        value={statsFormData.cardType || ""}
                        onChange={handleStatsChange}
                        className="w-full bg-surface-800 text-white rounded px-3 py-2"
                        required
                      >
                        <option value="">Select Card Type</option>
                        <option value="yellow">Yellow Card</option>
                        <option value="red">Red Card</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">
                        Reason (Optional)
                      </label>
                      <input
                        type="text"
                        name="reason"
                        value={statsFormData.reason || ""}
                        onChange={handleStatsChange}
                        className="w-full bg-surface-800 text-white rounded px-3 py-2"
                        placeholder="e.g., Foul, Dissent, Handball"
                      />
                    </div>
                  </>
                )}

                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    type="submit"
                    className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded"
                  >
                    Add Statistics
                  </button>
                  <button
                    onClick={() => {
                      setShowAddStatsForm(null);
                      resetStatsFormData();
                    }}
                    className="bg-surface-700 hover:bg-surface-700 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Substitutes Modal */}
      {showSubstitutesModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-surface-900 rounded-2xl p-6 w-full max-w-4xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">
                Substitutes - {showSubstitutesModal.homeTeam?.name || showSubstitutesModal.HomeTeam?.name} vs {showSubstitutesModal.awayTeam?.name || showSubstitutesModal.AwayTeam?.name}
              </h3>
              <button
                onClick={() => setShowSubstitutesModal(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Home Team Substitutes */}
              <div className="bg-surface-800 p-4 rounded-xl">
                <h4 className="text-xl font-bold text-accent-soft mb-4">
                  {showSubstitutesModal.homeTeam?.name || showSubstitutesModal.HomeTeam?.name} Substitutes
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {substitutes[showSubstitutesModal.matchesId]
                    ?.filter(sub => sub.Player.clubId === showSubstitutesModal.homeTeamId)
                    .map((substitute) => (
                      <div key={substitute.id} className="bg-surface-900 border border-line rounded-lg p-4 flex items-center space-x-3">
                        <img 
                          src={substitute.Player.profileImageUrl} 
                          alt={substitute.Player.name}
                          className="w-12 h-12 rounded-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/48x48/666666/FFFFFF?text=?';
                          }}
                        />
                        <div className="flex-1">
                          <h5 className="text-white font-semibold">{substitute.Player.name}</h5>
                          <p className="text-gray-300 text-sm">Position: {substitute.Player.position}</p>
                          <p className="text-gray-400 text-xs">Age: {substitute.Player.age}</p>
                        </div>
                      </div>
                    ))}
                  {substitutes[showSubstitutesModal.matchesId]
                    ?.filter(sub => sub.Player.clubId === showSubstitutesModal.homeTeamId).length === 0 && (
                    <div className="col-span-full text-center text-gray-400 py-8">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      No substitutes available for home team
                    </div>
                  )}
                </div>
              </div>

              {/* Away Team Substitutes */}
              <div className="bg-surface-800 p-4 rounded-xl">
                <h4 className="text-xl font-bold text-accent-soft mb-4">
                  {showSubstitutesModal.awayTeam?.name || showSubstitutesModal.AwayTeam?.name} Substitutes
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {substitutes[showSubstitutesModal.matchesId]
                    ?.filter(sub => sub.Player.clubId === showSubstitutesModal.awayTeamId)
                    .map((substitute) => (
                      <div key={substitute.id} className="bg-surface-900 border border-line rounded-lg p-4 flex items-center space-x-3">
                        <img 
                          src={substitute.Player.profileImageUrl} 
                          alt={substitute.Player.name}
                          className="w-12 h-12 rounded-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/48x48/666666/FFFFFF?text=?';
                          }}
                        />
                        <div className="flex-1">
                          <h5 className="text-white font-semibold">{substitute.Player.name}</h5>
                          <p className="text-gray-300 text-sm">Position: {substitute.Player.position}</p>
                          <p className="text-gray-400 text-xs">Age: {substitute.Player.age}</p>
                        </div>
                      </div>
                    ))}
                  {substitutes[showSubstitutesModal.matchesId]
                    ?.filter(sub => sub.Player.clubId === showSubstitutesModal.awayTeamId).length === 0 && (
                    <div className="col-span-full text-center text-gray-400 py-8">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      No substitutes available for away team
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-line">
              <button
                onClick={() => setShowSubstitutesModal(null)}
                className="bg-surface-700 hover:bg-surface-700 text-white px-6 py-2 rounded-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsTab;
